// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { averageValues, extentFromValues } from "../core/math.js";
import {
  clampScore,
  continuousAnalysisRuns,
  evidenceIntervalSeconds,
  finiteValues,
  lowPowerEngineWorkPoint as engineWorkPoint,
  median,
  quantile,
  rollingMedianStep,
} from "./utils.js";
import {
  carbIceParameters,
} from "./detectors/carb-ice.js";

function medianForWindow(points, key) {
  return median(points.map((point) => point[key]).filter(Number.isFinite));
}

function endMedianChange(points, key) {
  const config = carbIceParameters.windowChange;
  const values = points.map((point) => point[key]).filter(Number.isFinite);
  if (values.length < Math.max(config.minValues, points.length * config.minShare)) return null;
  const third = Math.max(config.minSegmentSamples, Math.floor(values.length / config.segmentDivisor));
  const start = median(values.slice(0, third));
  const end = median(values.slice(-third));
  return Number.isFinite(start) && Number.isFinite(end) ? end - start : null;
}

function carbIceRiskForTemp(tempF) {
  if (!Number.isFinite(tempF)) return 0;
  for (const band of carbIceParameters.temperatureRiskBands) {
    const aboveMin = band.includeMin === false ? tempF > band.minF : tempF >= band.minF;
    const belowMax = band.includeMax === false ? tempF < band.maxF : tempF <= band.maxF;
    if (!aboveMin || !belowMax) continue;
    if (band.startRisk === band.endRisk) return band.startRisk;
    const ratio = (tempF - band.minF) / (band.maxF - band.minF);
    return band.startRisk + ratio * (band.endRisk - band.startRisk);
  }
  return 0;
}

function carbIceRiskForOat(oatF) {
  if (!Number.isFinite(oatF)) return 0;
  for (const band of carbIceParameters.oatRiskBands) {
    const aboveMin = band.includeMin === false ? oatF > band.minF : oatF >= band.minF;
    const belowMax = band.includeMax === false ? oatF < band.maxF : oatF <= band.maxF;
    if (!aboveMin || !belowMax) continue;
    if (band.startRisk === band.endRisk) return band.startRisk;
    const ratio = (oatF - band.minF) / (band.maxF - band.minF);
    return band.startRisk + ratio * (band.endRisk - band.startRisk);
  }
  return 1;
}

function carbIceRiskForPoint(point) {
  return carbIceRiskForTemp(point?.carbTemp) * carbIceRiskForOat(point?.oat);
}

function carbIceWorkPoint(point) {
  if (!Number.isFinite(point?.carbTemp)) return false;
  const phase = String(point.phase ?? "");
  if (["Startup", "Runup", "Ground Run", "Takeoff", "Shutdown"].includes(phase)) return false;
  return engineWorkPoint(point);
}

function carbIceWindowPhase(points) {
  const counts = new Map();
  points.forEach((point) => {
    const phase = point.phase || "Unknown";
    counts.set(phase, (counts.get(phase) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "Unknown";
}

function carbIceWindowSummary(window) {
  const config = carbIceParameters.candidateWindow;
  const third = Math.max(config.minSummarySegmentSamples, Math.floor(window.length / config.summarySegmentDivisor));
  const start = window.slice(0, third);
  const middle = window.slice(third, window.length - third);
  const end = window.slice(-third);
  const carbStart = medianForWindow(start, "carbTemp");
  const carbMiddle = medianForWindow(middle, "carbTemp");
  const carbEnd = medianForWindow(end, "carbTemp");
  const carbValues = window.map((point) => point.carbTemp).filter(Number.isFinite);
  const carbTempMedian = median(carbValues);
  const oatValues = window.map((point) => point.oat).filter(Number.isFinite);
  const oatMedian = median(oatValues);
  const riskValues = window.map(carbIceRiskForPoint);
  const risk = averageValues(riskValues);
  const rpmStart = medianForWindow(start, "rpm");
  const rpmMiddle = medianForWindow(middle, "rpm");
  const rpmEnd = medianForWindow(end, "rpm");
  const mapStart = medianForWindow(start, "map");
  const mapMiddle = medianForWindow(middle, "map");
  const mapEnd = medianForWindow(end, "map");
  const rpmChange = endMedianChange(window, "rpm");
  const mapChange = endMedianChange(window, "map");
  const fuelChange = endMedianChange(window, "fuelFlow");
  return {
    startTime: window[0]?.elapsedSeconds ?? 0,
    endTime: window.at(-1)?.elapsedSeconds ?? window.length - 1,
    durationSeconds: (window.at(-1)?.elapsedSeconds ?? window.length - 1) - (window[0]?.elapsedSeconds ?? 0),
    phase: carbIceWindowPhase(window),
    carbStart,
    carbMiddle,
    carbEnd,
    carbTempMedian,
    carbTempMin: extentFromValues(carbValues)?.min ?? null,
    carbTempMax: extentFromValues(carbValues)?.max ?? null,
    carbTempChange: Number.isFinite(carbStart) && Number.isFinite(carbEnd) ? carbEnd - carbStart : null,
    oatMedian,
    oatMin: extentFromValues(oatValues)?.min ?? null,
    oatMax: extentFromValues(oatValues)?.max ?? null,
    oatRisk: carbIceRiskForOat(oatMedian),
    risk,
    rpmStart,
    rpmMiddle,
    rpmEnd,
    rpmDropToMiddle: Number.isFinite(rpmStart) && Number.isFinite(rpmMiddle) ? rpmStart - rpmMiddle : null,
    rpmRecovery: Number.isFinite(rpmMiddle) && Number.isFinite(rpmEnd) ? rpmEnd - rpmMiddle : null,
    rpmChange,
    mapStart,
    mapMiddle,
    mapEnd,
    mapDropToMiddle: Number.isFinite(mapStart) && Number.isFinite(mapMiddle) ? mapStart - mapMiddle : null,
    mapRecovery: Number.isFinite(mapMiddle) && Number.isFinite(mapEnd) ? mapEnd - mapMiddle : null,
    mapChange,
    fuelChange,
  };
}

function carbIceCandidateFromWindow(window) {
  const trigger = carbIceParameters.trigger;
  const scoring = carbIceParameters.scoring;
  const summary = carbIceWindowSummary(window);
  if (summary.risk < trigger.minRisk) return null;
  const fuelConfounded = Number.isFinite(summary.fuelChange) && summary.fuelChange <= -trigger.fuelConfoundDropGph;
  const rpmDecay = Number.isFinite(summary.rpmChange) ? -summary.rpmChange : null;
  const mapDecay = Number.isFinite(summary.mapChange) ? -summary.mapChange : null;
  const heatRise = summary.carbTempChange ?? 0;
  const hasRpmRecovery = (summary.rpmDropToMiddle ?? 0) >= trigger.rpmDropToMiddle && (summary.rpmRecovery ?? 0) >= trigger.rpmRecovery;
  const hasMapRecovery = (summary.mapDropToMiddle ?? 0) >= trigger.mapDropToMiddle && (summary.mapRecovery ?? 0) >= trigger.mapRecovery;
  const heatRecovery = heatRise >= trigger.heatRiseF && (hasRpmRecovery || hasMapRecovery);

  if (heatRecovery) {
    const recoverySignal = Math.max(
      Number.isFinite(summary.rpmRecovery) ? summary.rpmRecovery / scoring.rpmRecoveryDivisor : 0,
      Number.isFinite(summary.mapRecovery) ? summary.mapRecovery / scoring.mapRecoveryDivisor : 0,
    );
    return {
      ...summary,
      mode: "heatRecovery",
      suspicion: scoring.heatRecoveryBase
        + summary.risk * scoring.heatRecoveryRiskWeight
        + Math.min(scoring.heatRiseCap, heatRise * scoring.heatRiseWeight)
        + Math.min(scoring.recoverySignalCap, recoverySignal * scoring.recoverySignalWeight),
    };
  }

  const hasPowerDecay = (Number.isFinite(rpmDecay) && rpmDecay >= trigger.rpmPowerDecay) || (Number.isFinite(mapDecay) && mapDecay >= trigger.mapPowerDecay);
  if (!hasPowerDecay || fuelConfounded) return null;
  return {
    ...summary,
    mode: "powerDecay",
    suspicion: scoring.powerDecayBase
      + summary.risk * scoring.powerDecayRiskWeight
      + Math.min(scoring.rpmDecayCap, Math.max(0, rpmDecay ?? 0) * scoring.rpmDecayWeight)
      + Math.min(scoring.mapDecayCap, Math.max(0, mapDecay ?? 0) * scoring.mapDecayWeight)
      + (Number.isFinite(summary.fuelChange) && Math.abs(summary.fuelChange) <= trigger.stableFuelChangeGph ? scoring.stableFuelBonus : 0),
  };
}

function findCarbIceCandidates(data) {
  const config = carbIceParameters.candidateWindow;
  const runs = continuousAnalysisRuns(data, point => carbIceWorkPoint(point) && Number.isFinite(point.oat), carbIceParameters.coverage);
  const candidates = [];
  const evaluatedWindows = [];
  for (const rows of runs) {
    if (rows.length < config.minSamples) continue;
    const step = rollingMedianStep(rows);
    const sampleCount = Math.min(rows.length, Math.max(config.minSamples, Math.round(config.windowSeconds / step)));
    const stride = Math.max(config.minStrideSamples, Math.round(sampleCount / config.strideDivisor));
    for (let start = 0; start + sampleCount <= rows.length; start += stride) {
      const window = rows.slice(start, start + sampleCount);
      if (window.at(-1).elapsedSeconds - window[0].elapsedSeconds < carbIceParameters.coverage.minSeconds) continue;
      // Carb temperature alone cannot establish absence of a power-loss cue.
      const powerKeys = ["rpm", "map"].filter(key => window.every(point => Number.isFinite(point[key])));
      if (!powerKeys.length) continue;
      evaluatedWindows.push({ start: window[0].elapsedSeconds, end: window.at(-1).elapsedSeconds,
        sampleCount: window.length, powerKeys });
      const candidate = carbIceCandidateFromWindow(window);
      if (candidate) candidates.push(candidate);
    }
  }
  return { candidates: candidates.sort((a, b) => b.suspicion - a.suspicion), evaluatedWindows };
}

export function analyzeCarbIce(data) {
  const carbSamples = finiteValues(data, "carbTemp");
  const oatSamples = finiteValues(data, "oat");
  if (!carbSamples.length) {
    return { key: "carbIce", score: null, note: "No carb temperature channel", details: {} };
  }
  if (!oatSamples.length) {
    return { key: "carbIce", score: null, note: "No OAT channel for carb-ice screen", details: { carbSamples: carbSamples.length, oatSamples: 0 } };
  }
  if (carbSamples.length < carbIceParameters.minCarbTempSamples) {
    return { key: "carbIce", score: null, note: "Need more carb temperature samples", details: { carbSamples: carbSamples.length, oatSamples: oatSamples.length } };
  }
  if (oatSamples.length < carbIceParameters.minOatSamples) {
    return { key: "carbIce", score: null, note: "Need more OAT samples for carb-ice screen", details: { carbSamples: carbSamples.length, oatSamples: oatSamples.length } };
  }

  const analysisData = data;
  const workSamples = analysisData.filter(carbIceWorkPoint).length;
  if (workSamples < carbIceParameters.minWorkSamples) {
    return { key: "carbIce", score: null, note: "Need partial-power carb temperature/OAT samples", details: { carbSamples: carbSamples.length, oatSamples: oatSamples.length, workSamples } };
  }

  const { candidates, evaluatedWindows } = findCarbIceCandidates(analysisData);
  const evaluatedSeconds = evidenceIntervalSeconds(evaluatedWindows);
  const reasonCodes = [
    ...(!evaluatedWindows.length ? ["no-synchronized-power-window"] : []),
    ...(evaluatedSeconds < carbIceParameters.coverage.fullSeconds ? ["limited-continuous-coverage"] : []),
  ];
  const coverage = { status: evaluatedWindows.length ? "evaluated" : "unavailable",
    evaluatedSeconds, windows: evaluatedWindows.length, evaluatedWindows };
  const confidence = { label: reasonCodes.length ? "Low" : "Medium", reasonCodes };
  const riskP95 = quantile(analysisData.filter(point => carbIceWorkPoint(point) && Number.isFinite(point.oat)).map(carbIceRiskForPoint), 0.95) ?? null;
  if (!candidates.length) {
    return {
      key: "carbIce",
      score: evaluatedWindows.length ? 100 : null,
      note: !evaluatedWindows.length ? "Need continuous carb temperature, OAT, and RPM or MAP evidence"
        : riskP95 >= carbIceParameters.riskReviewThreshold ? "Carb temp entered icing range without power-loss cue" : "No carb-ice power-loss cue",
      details: { carbSamples: carbSamples.length, oatSamples: oatSamples.length,
        workSamples, riskP95, coverage, confidence, candidates: [] },
    };
  }

  const top = candidates[0];
  const score = clampScore(100 - Math.min(carbIceParameters.scoring.scoreSuspicionCap, top.suspicion));
  const temp = Number.isFinite(top.carbTempMedian) ? `${Math.round(top.carbTempMedian)} F` : "icing range";
  const note = top.mode === "heatRecovery"
    ? `Carb temp ${temp} with heat/recovery pattern`
    : Number.isFinite(top.rpmChange)
      ? `Carb temp ${temp} with RPM decay`
      : `Carb temp ${temp} with MAP decay`;
  return {
    key: "carbIce",
    score,
    note,
    details: {
      top,
      candidates,
      coverage,
      confidence,
      riskP95,
      carbSamples: carbSamples.length,
      oatSamples: oatSamples.length,
      workSamples,
    },
  };
}
